'use client';

import Link from '@/components/SiteLink';
import { useEffect, useRef, useState } from 'react';
import type { SiteConfig } from '@/server.api/types';

type HeaderMenuProps = {
    items: HeaderMenuItem[];
    navigation: SiteConfig['navigation'];
};

export type HeaderMenuItem = {
    id: string;
    title: string;
    href: string;
    children: Array<{
        id: string;
        title: string;
        href: string;
    }>;
};

export default function HeaderMenu({ items, navigation }: HeaderMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const close = (restoreFocus: boolean) => {
            setIsOpen(false);
            if (restoreFocus) window.requestAnimationFrame(() => buttonRef.current?.focus());
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close(true);
        };
        const keepFocusInside = (event: KeyboardEvent) => {
            if (event.key !== 'Tab' || !rootRef.current) return;

            const focusable = Array.from(
                rootRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]'),
            ).filter((element) => element.getClientRects().length > 0);
            const first = focusable.at(0);
            const last = focusable.at(-1);
            if (!first || !last) return;

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        const closeOutside = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) close(false);
        };

        document.addEventListener('keydown', closeOnEscape);
        document.addEventListener('keydown', keepFocusInside);
        document.addEventListener('pointerdown', closeOutside);
        window.requestAnimationFrame(() =>
            rootRef.current?.querySelector<HTMLElement>('.site-menu__directory-link')?.focus(),
        );

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', closeOnEscape);
            document.removeEventListener('keydown', keepFocusInside);
            document.removeEventListener('pointerdown', closeOutside);
        };
    }, [isOpen]);

    const closeMenu = () => setIsOpen(false);

    return (
        <div className='site-menu' ref={rootRef}>
            <nav className='site-menu__primary' aria-label={navigation.mainAriaLabel}>
                <ul className='site-menu__list'>
                    {items.map((category) => (
                        <li key={category.id} className='site-menu__item'>
                            <Link href={category.href} className='site-menu__link'>
                                {category.title}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            <button
                ref={buttonRef}
                type='button'
                className='site-menu__button'
                aria-label={isOpen ? navigation.closeMenuLabel : navigation.openMenuLabel}
                aria-expanded={isOpen}
                aria-controls='site-menu-directory'
                onClick={() => setIsOpen((current) => !current)}
            >
                <span className='site-menu__button-line' aria-hidden='true' />
                <span className='site-menu__button-line' aria-hidden='true' />
                <span className='site-menu__button-line' aria-hidden='true' />
            </button>

            <nav
                id='site-menu-directory'
                className='site-menu__directory'
                aria-label={navigation.mainAriaLabel}
                hidden={!isOpen}
            >
                <ul className='site-menu__directory-list'>
                    {items.map((category) => {
                        const children = category.children;
                        return (
                            <li key={category.id} className='site-menu__directory-group'>
                                <Link
                                    href={category.href}
                                    className='site-menu__directory-link'
                                    onClick={closeMenu}
                                >
                                    {category.title}
                                </Link>
                                {children.length > 0 && (
                                    <ul className='site-menu__sublist'>
                                        {children.map((subcategory) => (
                                            <li key={subcategory.id} className='site-menu__subitem'>
                                                <Link
                                                    href={subcategory.href}
                                                    className='site-menu__sublink'
                                                    onClick={closeMenu}
                                                >
                                                    {subcategory.title}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>
        </div>
    );
}
